

import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'


interface ScreenPosition {
  x: number
  y: number
}

/**
 * Convert 3D position to screen position
 */
export function toScreenPosition(
  obj: THREE.Object3D,
  camera: THREE.Camera,
  renderer: THREE.WebGLRenderer | EffectComposer
): ScreenPosition {
  const vector = new THREE.Vector3()

  // Handle both WebGLRenderer and EffectComposer
  const actualRenderer = (renderer as any).renderer || renderer
  const widthHalf = 0.5 * actualRenderer.domElement.width
  const heightHalf = 0.5 * actualRenderer.domElement.height

  obj.updateMatrixWorld()
  vector.setFromMatrixPosition(obj.matrixWorld)
  vector.project(camera)

  vector.x = Math.floor((vector.x * widthHalf) + widthHalf)
  vector.y = Math.floor(-(vector.y * heightHalf) + heightHalf)

  return { x: vector.x, y: vector.y }
}

/**
 * Check if object is visible on canvas
 */
export const isOnCanvas = (object: THREE.Object3D, camera: THREE.Camera): boolean => {
  // Force world matrix update on both
  object.updateWorldMatrix(true, false)
  camera.updateMatrixWorld()

  // Get actual world position (not just object.position, which is local)
  const worldPos = new THREE.Vector3()
  object.getWorldPosition(worldPos)

  const frustum = new THREE.Frustum()
  frustum.setFromProjectionMatrix(
    new THREE.Matrix4().multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse
    )
  )

  return frustum.containsPoint(worldPos)
}


/**
 * Frame camera to view entire model
 */
export const frameArea = (
  model: THREE.Object3D,
  camera: THREE.PerspectiveCamera,
  controls: any | null
): void => {
  const box = new THREE.Box3().setFromObject(model)
  const boxSize = box.getSize(new THREE.Vector3()).length()
  const boxCenter = box.getCenter(new THREE.Vector3())
  const sizeToFitOnScreen = boxSize * 0.5
  const halfSizeToFitOnScreen = sizeToFitOnScreen * 0.5
  const halfFovY = THREE.MathUtils.degToRad(camera.fov * 0.5)
  const distance = halfSizeToFitOnScreen / Math.tan(halfFovY)

  const direction = (new THREE.Vector3())
    .subVectors(camera.position, boxCenter)
    .multiply(new THREE.Vector3(1, 0, 1))
    .normalize()

  camera.position.copy(direction.multiplyScalar(distance).add(boxCenter))

  camera.near = boxSize / 100
  camera.far = boxSize * 100
  camera.updateProjectionMatrix()

  camera.lookAt(boxCenter.x, boxCenter.y, boxCenter.z)

  if (controls != null) {
    controls.maxDistance = boxSize * 10
    controls.target.copy(boxCenter)
    controls.update()
  }
}
